import {
  Box,
  Button,
  CheckboxOption,
  Input,
  Select,
  Textarea,
} from '@nerdwallet/currency';
import DOMPurify from 'isomorphic-dompurify';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import AlignCenterIcon from '@nerdwallet/react-icon/fontawesome/AlignCenter';
import AlignLeftIcon from '@nerdwallet/react-icon/fontawesome/AlignLeft';
import AlignRightIcon from '@nerdwallet/react-icon/fontawesome/AlignRight';
import BoldIcon from '@nerdwallet/react-icon/fontawesome/Bold';
import DatabaseIcon from '@nerdwallet/react-icon/fontawesome/Database';
import ItalicIcon from '@nerdwallet/react-icon/fontawesome/Italic';
import StrikethroughIcon from '@nerdwallet/react-icon/fontawesome/Strikethrough';
import UnderlineIcon from '@nerdwallet/react-icon/fontawesome/Underline';

import Accordion from '@nerdwallet/react-accordion';
import TextIcon from '@nerdwallet/react-icon/material/TextFields';
import { ColorDropdown } from '~/app/lib/color-picker/color-picker';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useMediaQuery } from '~/app/lib/use-media-query';
import { usePathname } from 'next/navigation';
import { isInBuilder } from '~/app/lib/helpers';
import { DynamicTextButton } from './dynamic-text-button';
import { fontFamilyOptions, useDynamicText } from './use-dynamic-text';

import { DynamicMarketplaceOfferViewerComponent } from '../dynamic-marketplace-offer-viewer/dynamic-marketplace-offer-viewer-component';
import styles from './dynamic-text.module.scss';
import { useDynamicTextInterpolation } from './use-dynamic-text-interpolation';
import { useDynamicAnalytics } from '../../segment/use-dynamic-analytics';

export type DynamicTextComponentProps = {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  textAlign?: string;
  fontSize?: number;
  color?: string;
  hexColor?: string;
  marketplaceOfferId?: string;
  pressReleaseId?: string;
  fontFamily?: string;
  lineHeight?: string;
  letterSpacing?: string;
  mobile?: { fontSize: number; lineHeight?: string };
  tablet?: { fontSize: number; lineHeight?: string };
  interpolationSource?: string[];
  defaultEmptyText?: string;
  interpolatedLinkItems?: {
    key: string;
    value: string;
    impressionSection: string;
    impressionPosition: string;
  }[];
  enableApplyNowButtonTracking?: boolean;
  captureCustomQueryParams?: boolean;
  textToShowCb?: (textToShow: string) => void;
};

const ICON_SELECTED_COLOR = '#008254';
const ICON_DEFAULT_COLOR = '#000';

const getIconColor = (isSelectedCondition: boolean) => {
  return isSelectedCondition ? ICON_SELECTED_COLOR : ICON_DEFAULT_COLOR;
};

const htmlOutputParser = (content: string, enableHtml: boolean) => {
  if (enableHtml) {
    return (
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content),
        }}
      />
    );
  }
  return content;
};

export const DynamicTextComponent: React.FC<DynamicTextComponentProps> = ({
  text = '',
  bold = false,
  italic = false,
  underline = false,
  strikethrough = false,
  textAlign = 'left',
  fontSize = 16,
  color = '#000',
  hexColor = '',
  marketplaceOfferId = '',
  pressReleaseId = '',
  fontFamily = 'Default',
  lineHeight = 'normal',
  letterSpacing = 'normal',
  mobile = { fontSize: 16, lineHeight: 'normal' },
  tablet = { fontSize: 16, lineHeight: 'normal' },
  interpolationSource = ['marketplaceOffer'],
  defaultEmptyText = '',
  interpolatedLinkItems = [],
  enableApplyNowButtonTracking = false,
  captureCustomQueryParams = false,
  textToShowCb,
}: DynamicTextComponentProps) => {
  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);

  const { textToShow: interpolatedTextToShow, hasInterpolatedLinks } =
    useDynamicTextInterpolation(
      text,
      marketplaceOfferId,
      pressReleaseId,
      interpolationSource,
      interpolatedLinkItems,
      enableApplyNowButtonTracking,
      captureCustomQueryParams
    );

  let textToShow = interpolatedTextToShow;

  const { handleTrackInteraction } = useDynamicAnalytics({
    enableInteractionTracking: true,
  });

  const handleLinkClick = useCallback(
    (event: React.MouseEvent<HTMLParagraphElement>) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('interpolated-link')) {
        const impressionSection = target.getAttribute(
          'data-impression-section'
        );
        const impressionPosition = target.getAttribute(
          'data-impression-position'
        );

        handleTrackInteraction({
          impressionSectionOverride: impressionSection ?? '',
          impressionPositionOverride: impressionPosition ?? '',
        });
      }
    },
    [handleTrackInteraction]
  );

  const textDecoration = useCallback(() => {
    if (underline) {
      return 'underline';
    }
    if (strikethrough) {
      return 'line-through';
    }
    return 'none';
  }, [underline, strikethrough]);

  const fontFamilyClass = useMemo(() => {
    const fontFamilyMap = {
      Chronicle: styles.fontFamilyChronicle,
      Obviously: styles.fontFamilyObviously,
      GothamBlack: styles.fontFamilyGothamBlack,
      Default: styles.fontFamilyDefault,
    };
    return (
      fontFamilyMap[fontFamily as keyof typeof fontFamilyMap] ||
      styles.fontFamilyDefault
    );
  }, [fontFamily]);

  const colorValue = color === 'HEXCOLOR' ? hexColor : color;

  const mediaQuery = useMediaQuery();

  const fontSizeMediaQueryBased = useMemo(() => {
    if (mediaQuery === 'sm') {
      return mobile.fontSize || fontSize;
    }
    if (mediaQuery === 'md') {
      return tablet.fontSize || fontSize;
    }
    return fontSize;
  }, [mediaQuery, mobile?.fontSize, tablet?.fontSize, fontSize]);

  const lineHeightMediaQueryBased = useMemo(() => {
    if (mediaQuery === 'sm') {
      return mobile.lineHeight || lineHeight;
    }
    if (mediaQuery === 'md') {
      return tablet.lineHeight || lineHeight;
    }
    return lineHeight;
  }, [mediaQuery, mobile?.lineHeight, tablet?.lineHeight, lineHeight]);

  if (defaultEmptyText && !textToShow) {
    textToShow = defaultEmptyText;
  }

  if (textToShowCb) {
    textToShowCb(textToShow);
  }

  if (!textToShow && isBuilderPage) {
    return <TextIcon size={64} />;
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <p
      className={fontFamilyClass}
      style={{
        color: colorValue,
        fontWeight: bold ? 'bold' : 'normal',
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: textDecoration(),
        textAlign: textAlign as 'left' | 'center' | 'right' | 'justify',
        fontSize: `${fontSizeMediaQueryBased}px`,
        lineHeight:
          lineHeightMediaQueryBased === 'normal'
            ? '1.5'
            : lineHeightMediaQueryBased,
        letterSpacing: letterSpacing === 'normal' ? '0' : letterSpacing,
      }}
      onClick={hasInterpolatedLinks ? handleLinkClick : undefined}
    >
      {htmlOutputParser(textToShow, true)}
    </p>
  );
};

export type inputsSections =
  | 'marketplaceOffer'
  | 'textControls'
  | 'alignControls'
  | 'fontControls';

type DynamicTextFormProps = {
  fieldPath?: string;
  disabledInputs?: inputsSections[];
  componentType?: 'textArea' | 'input';
  marketplaceOfferOverride?: string;
};

export const DynamicTextForm = ({
  fieldPath = '',
  disabledInputs = [],
  componentType = 'textArea',
  marketplaceOfferOverride = '',
}: DynamicTextFormProps) => {
  const { selected: selectedComponentId } = useBuilder();
  const { marketplaceOffers } = useDynamicGrid({
    componentId: selectedComponentId,
  });

  const {
    text,
    interpolationSource,
    fontSize,
    bold,
    italic,
    underline,
    strikethrough,
    textAlign,
    handleChange,
    handleInterpolationSourceChange,
    handleChangeFontSize,
    handleClickBold,
    handleClickItalic,
    handleClickUnderline,
    handleClickStrikethrough,
    handleClickTextAlign,
    handleChangeColor,
    color,
    handleChangeHexColor,
    hexColor,
    marketplaceOfferId,
    pressReleaseId,
    textInterpolationsOptions,
    marketplaceOfferOptions,
    pressReleaseOptions,
    handleMarketplaceOfferChange,
    handlePressReleaseChange,
    fontFamily,
    handleChangeFontFamily,
    textAreaRef,
    lineHeight,
    letterSpacing,
    handleChangeLineHeight,
    handleChangeLetterSpacing,
    mobile,
    tablet,
    handleChangeMobileFontSize,
    handleChangeTabletFontSize,
    handleChangeMobileLineHeight,
    handleChangeTabletLineHeight,
    handleAddInterpolatedLink,
    handleDeleteInterpolatedLink,
    interpolatedLinkItems,
    handleChangeInterpolatedLinkValue,
    handleChangeDefaultEmptyText,
    defaultEmptyText,
    enableApplyNowButtonTracking,
    handleEnableApplyNowButtonTrackingChange,
    captureCustomQueryParams,
    handleCaptureCustomQueryParamsChange,
  } = useDynamicText({
    fieldPath,
  });

  const [
    isMarketplaceOfferViewerModalOpen,
    setIsMarketplaceOfferViewerModalOpen,
  ] = useState(false);

  const isHexColor = color === 'HEXCOLOR';

  // Function to handle marketplace offer field clicks and insert into textarea
  const handleMarketplaceOfferViewerFieldClick = (attributePath: string) => {
    if (!textAreaRef?.current) return;

    const textarea = textAreaRef.current;
    const cursorPosition = textarea.selectionStart;
    const currentText = text || '';

    // Format the field path as an interpolation placeholder

    // Remove [arrayIndex] from the beginning of attributePath
    const cleanAttributePath = attributePath.replace(/^\[\d+\]\.?/, '');
    const attributePlaceholder = `{{${
      interpolationSource.length > 1 ? 'marketplaceOffer.' : ''
    }${cleanAttributePath}}}`;

    // Insert the attribute at the cursor position
    const newText =
      currentText.slice(0, cursorPosition) +
      attributePlaceholder +
      currentText.slice(cursorPosition);

    // Update the text in the component
    handleChange({
      target: { value: newText },
    } as React.ChangeEvent<HTMLTextAreaElement>);

    // Set cursor position after the inserted text and focus the textarea
    setTimeout(() => {
      if (textarea) {
        const newCursorPosition = cursorPosition + attributePlaceholder.length;
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        textarea.focus();
      }
    }, 0);

    // Close the modal after selection
    setIsMarketplaceOfferViewerModalOpen(false);
  };

  useEffect(() => {
    // If marketplaceOfferOverride is provided, set it as the marketplaceOfferId
    if (marketplaceOfferOverride) {
      handleMarketplaceOfferChange({
        target: { value: marketplaceOfferOverride },
      } as React.ChangeEvent<HTMLSelectElement>);
    }
  }, [marketplaceOfferOverride, handleMarketplaceOfferChange]);

  const showMarketplaceOfferViewer = useMemo(() => {
    return (
      interpolationSource.includes('marketplaceOffer') &&
      marketplaceOffers?.length &&
      marketplaceOfferId
    );
  }, [interpolationSource, marketplaceOffers, marketplaceOfferId]);

  const showInterpolatedLinkViewer = useMemo(() => {
    return interpolationSource.includes('interpolatedLink');
  }, [interpolationSource]);

  const interpolatedLinkItemsAccordion = [
    {
      id: 'interpolatedLinkItems',
      title: 'Interpolated Link Items',
      children: interpolatedLinkItems.map(({ key, value }, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            <Box paddingY={2}>
              <Input
                fullWidth
                label="Key"
                value={key}
                onChange={(e) =>
                  handleChangeInterpolatedLinkValue(
                    index,
                    e.target.value,
                    'key'
                  )
                }
              />
            </Box>
            <Box paddingY={2}>
              <Input
                fullWidth
                label="Value"
                value={value}
                onChange={(e) =>
                  handleChangeInterpolatedLinkValue(
                    index,
                    e.target.value,
                    'value'
                  )
                }
              />
            </Box>
            <Box paddingY={2}>
              <Input
                fullWidth
                label="Impression Section"
                value={interpolatedLinkItems[index].impressionSection}
                onChange={(e) =>
                  handleChangeInterpolatedLinkValue(
                    index,
                    e.target.value,
                    'impressionSection'
                  )
                }
              />
            </Box>
            <Box paddingY={2}>
              <Input
                fullWidth
                label="Impression Position"
                value={interpolatedLinkItems[index].impressionPosition}
                onChange={(e) =>
                  handleChangeInterpolatedLinkValue(
                    index,
                    e.target.value,
                    'impressionPosition'
                  )
                }
              />
            </Box>
            <Box paddingY={2}>
              <Button
                block
                disabled={interpolatedLinkItems.length < 2}
                onClick={() => handleDeleteInterpolatedLink(index)}
              >
                Delete Interpolated Link
              </Button>
            </Box>
            <Box paddingY={2}>
              <Button block onClick={handleAddInterpolatedLink}>
                Add Interpolated Link
              </Button>
            </Box>
          </Fragment>
        );
      }),
    },
  ];

  return (
    <Fragment>
      <Box paddingY={2}>
        <Select
          multiple
          fullWidth
          value={interpolationSource}
          onChange={handleInterpolationSourceChange}
          label="Interpolation Source"
          options={textInterpolationsOptions()}
        />
      </Box>
      {!disabledInputs.includes('marketplaceOffer') &&
        interpolationSource?.includes('marketplaceOffer') &&
        (!!marketplaceOffers?.length || marketplaceOfferId) && (
          <Box paddingY={2}>
            <Select
              fullWidth
              value={marketplaceOfferId}
              onChange={handleMarketplaceOfferChange}
              label="Marketplace Offer"
              options={marketplaceOfferOptions()}
            />
          </Box>
        )}
      {interpolationSource?.includes('pressRelease') && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={pressReleaseId}
            onChange={handlePressReleaseChange}
            label="Press Release"
            options={pressReleaseOptions()}
          />
        </Box>
      )}
      <Box paddingY={2}>
        {componentType === 'textArea' && (
          <Textarea
            ref={textAreaRef}
            label="Text"
            fullWidth
            value={text || ''}
            onChange={handleChange}
            className={styles.textArea}
          />
        )}
        {componentType === 'input' && (
          <Input
            ref={textAreaRef as unknown as React.RefObject<HTMLInputElement>}
            label="Text"
            fullWidth
            value={text || ''}
            onChange={handleChange}
          />
        )}
      </Box>
      <Box paddingRight={2}>
        <Input
          label="Default Empty Text"
          value={defaultEmptyText || ''}
          onChange={handleChangeDefaultEmptyText}
          fullWidth
        />
      </Box>
      <Box
        position="relative"
        display="flex"
        paddingY={2}
        justifyContent="space-around"
      >
        {!disabledInputs.includes('textControls') && (
          <Fragment>
            <DynamicTextButton
              label="Bold"
              isSelected={bold}
              onClick={handleClickBold}
              icon={<BoldIcon color={getIconColor(bold)} />}
            />
            <DynamicTextButton
              label="Italic"
              isSelected={italic}
              onClick={handleClickItalic}
              icon={<ItalicIcon color={getIconColor(italic)} />}
            />
            <DynamicTextButton
              label="Underline"
              isSelected={underline}
              onClick={handleClickUnderline}
              icon={<UnderlineIcon color={getIconColor(underline)} />}
            />
            <DynamicTextButton
              label="Strikethrough"
              isSelected={strikethrough}
              onClick={handleClickStrikethrough}
              icon={<StrikethroughIcon color={getIconColor(strikethrough)} />}
            />
          </Fragment>
        )}
        {!disabledInputs.includes('alignControls') && (
          <Fragment>
            <DynamicTextButton
              label="Align Left"
              isSelected={textAlign === 'left'}
              onClick={() => handleClickTextAlign('left')}
              icon={
                <AlignLeftIcon color={getIconColor(textAlign === 'left')} />
              }
            />
            <DynamicTextButton
              label="Align Center"
              isSelected={textAlign === 'center'}
              onClick={() => handleClickTextAlign('center')}
              icon={
                <AlignCenterIcon color={getIconColor(textAlign === 'center')} />
              }
            />
            <DynamicTextButton
              label="Align Right"
              isSelected={textAlign === 'right'}
              onClick={() => handleClickTextAlign('right')}
              icon={
                <AlignRightIcon color={getIconColor(textAlign === 'right')} />
              }
            />
          </Fragment>
        )}
        {!!showMarketplaceOfferViewer && (
          <Fragment>
            <DynamicMarketplaceOfferViewerComponent
              marketplaceOffers={marketplaceOffers}
              isModalOpen={isMarketplaceOfferViewerModalOpen}
              setIsModalOpen={setIsMarketplaceOfferViewerModalOpen}
              handleFieldClick={handleMarketplaceOfferViewerFieldClick}
              openModalForRule={() =>
                setIsMarketplaceOfferViewerModalOpen(true)
              }
              overrideButtonIcon={<DatabaseIcon size="16px" color="#000" />}
              overrideButtonStyle={{ padding: '0' }}
            />
          </Fragment>
        )}
      </Box>

      <Box paddingY={2}>
        <CheckboxOption
          label="Enable Apply Now Button Tracking?"
          name="enableApplyNowButtonTracking"
          checked={enableApplyNowButtonTracking}
          onChange={handleEnableApplyNowButtonTrackingChange}
        />
      </Box>

      <Box paddingY={2}>
        <CheckboxOption
          label="Capture Custom Query Params?"
          name="captureCustomQueryParams"
          checked={captureCustomQueryParams}
          onChange={handleCaptureCustomQueryParamsChange}
        />
      </Box>

      {!!showInterpolatedLinkViewer && (
        <Box className={styles.accordionWrapper}>
          <Accordion items={interpolatedLinkItemsAccordion} />
        </Box>
      )}

      {!disabledInputs.includes('fontControls') && (
        <Fragment>
          <Box paddingY={2} display="flex" gap={2}>
            <Box paddingRight={2}>
              <Input
                label="Font Size"
                fullWidth
                value={fontSize || 16}
                onChange={handleChangeFontSize}
                type="number"
              />
            </Box>
            <Box>
              <Select
                fullWidth
                value={fontFamily || 'Default'}
                onChange={handleChangeFontFamily}
                label="Font Family"
                options={fontFamilyOptions}
              />
            </Box>
          </Box>

          <Box paddingTop={2} display="flex">
            <Box width={isHexColor ? 70 : 100}>
              <ColorDropdown
                label="Select a text Color"
                onChange={(colorHex) => handleChangeColor(colorHex)}
                initialColor={color}
              />
            </Box>
            {color === 'HEXCOLOR' && (
              <Box width={30} paddingLeft={2}>
                <Input
                  fullWidth
                  label="HexColor"
                  onChange={handleChangeHexColor}
                  value={hexColor || ''}
                />
              </Box>
            )}
          </Box>

          <Box paddingBottom={2} display="flex">
            <Box paddingRight={2} width={50}>
              <Input
                label="Line Height"
                value={lineHeight || 'normal'}
                onChange={handleChangeLineHeight}
                fullWidth
              />
            </Box>
            <Box width={50}>
              <Input
                label="Letter Spacing"
                value={letterSpacing || 'normal'}
                onChange={handleChangeLetterSpacing}
                fullWidth
              />
            </Box>
          </Box>
          <Box>
            <Accordion
              items={[
                {
                  id: 'tablet',
                  title: 'Tablet',
                  children: (
                    <Fragment>
                      <Input
                        label="Font Size"
                        value={tablet.fontSize || 16}
                        onChange={handleChangeTabletFontSize}
                        type="number"
                        fullWidth
                      />
                      <Input
                        label="Line Height"
                        value={tablet.lineHeight || 'normal'}
                        onChange={handleChangeTabletLineHeight}
                        fullWidth
                      />
                    </Fragment>
                  ),
                },
                {
                  id: 'mobile',
                  title: 'Mobile',
                  children: (
                    <Fragment>
                      <Input
                        label="Font Size"
                        value={mobile.fontSize || 16}
                        onChange={handleChangeMobileFontSize}
                        type="number"
                        fullWidth
                      />
                      <Input
                        label="Line Height"
                        value={mobile.lineHeight || 'normal'}
                        onChange={handleChangeMobileLineHeight}
                        fullWidth
                      />
                    </Fragment>
                  ),
                },
              ]}
            />
          </Box>
        </Fragment>
      )}
    </Fragment>
  );
};
